/**
 * Registers the .ttf files the app already ships with as CSS @font-face rules.
 *
 * react-native-vector-icons renders glyphs by setting fontFamily to the icon
 * set name (e.g. "MaterialCommunityIcons"). On native the font is linked at
 * build time; in a browser the family has to exist in CSS first, otherwise
 * every icon renders as a tofu box.
 */

// Icon sets used by zen-core-elements/ZenIcon plus the app's brand font.
const iconFonts: Record<string, string> = {
  AntDesign: require('react-native-vector-icons/Fonts/AntDesign.ttf'),
  Entypo: require('react-native-vector-icons/Fonts/Entypo.ttf'),
  EvilIcons: require('react-native-vector-icons/Fonts/EvilIcons.ttf'),
  Feather: require('react-native-vector-icons/Fonts/Feather.ttf'),
  FontAwesome: require('react-native-vector-icons/Fonts/FontAwesome.ttf'),
  FontAwesome5_Brands: require('react-native-vector-icons/Fonts/FontAwesome5_Brands.ttf'),
  FontAwesome5_Regular: require('react-native-vector-icons/Fonts/FontAwesome5_Regular.ttf'),
  FontAwesome5_Solid: require('react-native-vector-icons/Fonts/FontAwesome5_Solid.ttf'),
  Fontisto: require('react-native-vector-icons/Fonts/Fontisto.ttf'),
  Foundation: require('react-native-vector-icons/Fonts/Foundation.ttf'),
  Ionicons: require('react-native-vector-icons/Fonts/Ionicons.ttf'),
  MaterialCommunityIcons: require('react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf'),
  MaterialIcons: require('react-native-vector-icons/Fonts/MaterialIcons.ttf'),
  Octicons: require('react-native-vector-icons/Fonts/Octicons.ttf'),
  SimpleLineIcons: require('react-native-vector-icons/Fonts/SimpleLineIcons.ttf'),
  Zocial: require('react-native-vector-icons/Fonts/Zocial.ttf'),
};

const appFonts: Record<string, string> = {
  'Montserrat-Medium': require('../../assets/fonts/Montserrat-Medium.ttf'),
  'Montserrat-SemiBold': require('../../assets/fonts/Montserrat-SemiBold.ttf'),
};

const STYLE_ELEMENT_ID = 'zenbasket-pos-fonts';

/**
 * Locally installed faces to fall back to when the bundled .ttf cannot be
 * fetched. Declaring them as additional `src` entries of the *same* family
 * means a style that says `font-family: Montserrat-Medium` still resolves to a
 * real UI font instead of dropping to the browser's serif default - screens
 * degrade together rather than one screen at a time.
 */
const brandFallbacks = "local('Segoe UI'), local('Roboto'), local('Helvetica Neue')";

function toFontFace(family: string, url: string, fallbacks?: string): string {
  const sources = [`url(${url}) format('truetype')`, fallbacks]
    .filter(Boolean)
    .join(', ');
  return `@font-face {
  src: ${sources};
  font-family: '${family}';
  font-display: block;
}`;
}

export function loadWebFonts(): void {
  if (typeof document === 'undefined') {
    return;
  }
  // runApplication can be called more than once during hot reloads.
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }

  const rules = [
    // Icon sets have no sane fallback - a missing glyph font must stay missing
    // rather than render a letter from a text font in its place.
    ...Object.entries(iconFonts).map(([family, url]) => toFontFace(family, url)),
    ...Object.entries(appFonts).map(([family, url]) =>
      toFontFace(family, url, brandFallbacks),
    ),
  ].join('\n');

  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.appendChild(document.createTextNode(rules));
  document.head.appendChild(style);
}
